import clsx from "clsx";
import Tooltip from "@/components/library/Tooltip";
import * as React from "react";
import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {GenericComponentTypes} from "@/types/common/library";
import {Combobox} from "@headlessui/react";
import ComboBoxDisplay, {SelectOption} from "./ComboBoxDisplay";
import Icon, {ICON_SIZES, ICON_TYPE} from "./Icon";

type SelectProps = {
   options: SelectOption[];
   label?: string;
   tooltipText?: string;
   isMulti?: boolean;
   helperText?: string;
   placeHolder?: string;
   disabled?: boolean;
   value?: string | SelectOption[];
   id?: string;
   name?: string;
   isClearable?: boolean;
   onChange?: Function;
   onBlur?: Function;
   required?: boolean;
   error?: boolean;
   warning?: boolean;
   allowCustomValue?: boolean;
   /** Accessible name when no visible `label` is rendered. */
   ariaLabel?: string;
} & GenericComponentTypes;

const Select: React.FC<SelectProps> = ({
   label,
   tooltipText,
   options,
   isMulti,
   helperText,
   placeHolder,
   disabled,
   value,
   id,
   name,
   isClearable,
   required,
   ["data-testid"]: _dataTestId,
   error,
   warning,
   onChange,
   onBlur,
   allowCustomValue,
   ariaLabel,
}) => {
   const {t: ct} = useTranslation("common");

   const [selectedOptions, setSelectedOptions] = useState(
      isMulti
         ? (value as SelectOption[]) || ([] as SelectOption[])
         : (value && options.find((o) => o.value === value)) || ({} as SelectOption)
   );
   const [query, setQuery] = useState("");
   const [isOpen, setIsOpen] = useState(false);
   const [highlightedIndex, setHighlightedIndex] = useState(0);
   const [canScrollDown, setCanScrollDown] = useState(false);
   const listboxId = React.useId();
   const comboboxContainerRef = React.useRef<HTMLDivElement>(null);
   const dropdownRef = React.useRef<HTMLDivElement>(null);
   const scrollContainerRef = React.useRef<HTMLDivElement>(null);

   // Check if dropdown can scroll down
   const checkScrollState = React.useCallback(() => {
      if (!scrollContainerRef.current) return;
      const {scrollTop, scrollHeight, clientHeight} = scrollContainerRef.current;
      setCanScrollDown(scrollTop + clientHeight < scrollHeight - 5);
   }, []);

   // Detect if we're inside a modal/dialog for z-index purposes
   const isInsideDialog = React.useMemo(() => {
      if (typeof window === "undefined") return false;
      return !!document.querySelector('[data-dialog-content][data-state="open"]');
   }, [isOpen]);

   const filteredOptions =
      query === ""
         ? options
         : (() => {
              const normalizedQuery = query.toLowerCase().replace(/\s+/g, "");
              return options.filter((option) => {
                 const haystack =
                    option.searchText ??
                    (typeof option.label === "string" ? option.label : option.value);
                 return haystack
                    .toLowerCase()
                    .replace(/\s+/g, "")
                    .includes(normalizedQuery);
              });
           })();

   const showCustomValueOption =
      !!allowCustomValue && query.length > 0 && filteredOptions.length === 0;
   const dropdownOptionCount = showCustomValueOption ? 1 : filteredOptions.length;

   const getInitialHighlightedIndex = React.useCallback((): number => {
      if (dropdownOptionCount === 0) return 0;

      if (!isMulti) {
         const selected = selectedOptions as SelectOption;
         if (selected?.value) {
            const selectedIndex = filteredOptions.findIndex(
               (option) => option.value === selected.value
            );
            if (selectedIndex >= 0) return selectedIndex;
         }
         return 0;
      }

      const selected = selectedOptions as SelectOption[];
      if (selected.length > 0) {
         const selectedIndex = filteredOptions.findIndex((option) =>
            selected.some((s) => s.value === option.value)
         );
         if (selectedIndex >= 0) return selectedIndex;
      }
      return 0;
   }, [dropdownOptionCount, isMulti, selectedOptions, filteredOptions]);

   useEffect(() => {
      if (!isOpen) {
         setQuery("");
      }
   }, [isOpen]);

   useEffect(() => {
      if (isOpen) {
         setHighlightedIndex(getInitialHighlightedIndex());
      }
   }, [isOpen, query, dropdownOptionCount, getInitialHighlightedIndex]);

   const scrollHighlightedOptionIntoView = React.useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const optionEl = document.getElementById(`${listboxId}-option-${highlightedIndex}`);
      if (!optionEl) return;

      const optionRect = optionEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      if (optionRect.bottom > containerRect.bottom) {
         container.scrollTop += optionRect.bottom - containerRect.bottom;
      } else if (optionRect.top < containerRect.top) {
         container.scrollTop -= containerRect.top - optionRect.top;
      }
   }, [highlightedIndex, listboxId]);

   useEffect(() => {
      if (!isOpen || dropdownOptionCount === 0) return;
      scrollHighlightedOptionIntoView();
   }, [highlightedIndex, isOpen, dropdownOptionCount, scrollHighlightedOptionIntoView]);

   useEffect(() => {
      if (!isOpen) return;
      comboboxContainerRef.current
         ?.querySelector<HTMLInputElement>("[data-combobox-input]")
         ?.focus();
   }, [isOpen]);

   useEffect(() => {
      setSelectedOptions(
         isMulti
            ? (value as SelectOption[]) || ([] as SelectOption[])
            : (value && options.find((o) => o.value === value)) || ({} as SelectOption)
      );
   }, [value]);

   // Shared function to update dropdown position
   const updateDropdownPosition = React.useCallback(() => {
      if (!comboboxContainerRef.current || !dropdownRef.current) return;

      const rect = comboboxContainerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 20; // 20px buffer from viewport edge
      const minDropdownHeight = 150; // Only flip if we can't show at least this much
      const shouldFlipUp = spaceBelow < minDropdownHeight && rect.top > spaceBelow;

      // Always use viewport-relative coordinates with fixed positioning
      if (shouldFlipUp) {
         dropdownRef.current.style.top = "auto";
         dropdownRef.current.style.bottom = `${window.innerHeight - rect.top + 4}px`;
         dropdownRef.current.style.maxHeight = `${Math.min(240, rect.top - 20)}px`;
      } else {
         dropdownRef.current.style.bottom = "auto";
         dropdownRef.current.style.top = `${rect.bottom + 4}px`;
         dropdownRef.current.style.maxHeight = `${Math.min(240, spaceBelow)}px`;
      }
      dropdownRef.current.style.left = `${rect.left}px`;
      dropdownRef.current.style.width = `${rect.width}px`;
   }, []);

   // Handle scroll and resize events to keep dropdown positioned correctly
   useEffect(() => {
      if (!isOpen || !dropdownRef.current) return;

      const handleClickOutside = (event: MouseEvent) => {
         // Use setTimeout to allow dropdown item clicks to register first
         setTimeout(() => {
            if (
               comboboxContainerRef.current &&
               !comboboxContainerRef.current.contains(event.target as Node) &&
               dropdownRef.current &&
               !dropdownRef.current.contains(event.target as Node)
            ) {
               setIsOpen(false);
            }
         }, 0);
      };

      const handleEscape = (event: KeyboardEvent) => {
         if (event.key === "Escape") {
            setIsOpen(false);
         }
      };

      // Initial position update
      updateDropdownPosition();

      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);

      return () => {
         window.removeEventListener("scroll", updateDropdownPosition, true);
         window.removeEventListener("resize", updateDropdownPosition);
         document.removeEventListener("mousedown", handleClickOutside);
         document.removeEventListener("keydown", handleEscape);
      };
   }, [isOpen, updateDropdownPosition]);

   // Set initial position when dropdown opens
   useEffect(() => {
      if (isOpen) {
         updateDropdownPosition();
         // Check scroll state after a short delay to allow DOM to render
         setTimeout(checkScrollState, 50);
      }
   }, [isOpen, updateDropdownPosition, checkScrollState]);

   // Listen for scroll events on the dropdown
   useEffect(() => {
      const scrollContainer = scrollContainerRef.current;
      if (!isOpen || !scrollContainer) return;

      scrollContainer.addEventListener("scroll", checkScrollState);
      return () => scrollContainer.removeEventListener("scroll", checkScrollState);
   }, [isOpen, checkScrollState]);

   const onSelectChange = (e: SelectOption) => {
      setQuery("");

      if (isMulti) {
         const oldSelected = selectedOptions as SelectOption[];
         const found = oldSelected.find((option) => e.value === option.value);
         if (!found) {
            const newValues = [e, ...oldSelected] as SelectOption[];

            setSelectedOptions(newValues);

            var event = {
               target: {name, value: newValues},
            } as unknown as React.SyntheticEvent;
            onChange && onChange(event);
         } else {
            var newSelections = selectedOptions as SelectOption[];
            const index = newSelections.findIndex((option) => option.value === e.value);
            newSelections.splice(index, 1);
            setSelectedOptions(newSelections);

            var event = {
               target: {name, value: newSelections},
            } as unknown as React.SyntheticEvent;
            onChange && onChange(event);
         }
      } else {
         let newValue = e as SelectOption;

         if (newValue.value === (selectedOptions as SelectOption).value) {
            newValue = {} as SelectOption;
         }

         setSelectedOptions(newValue);
         setIsOpen(false);
         var event = {
            target: {name, value: newValue.value || ""},
         } as unknown as React.SyntheticEvent;
         onChange && onChange(event);
      }
   };

   const checkIfSelected = (selected: boolean, option: SelectOption) => {
      return isMulti
         ? !!(selectedOptions as SelectOption[]).find(
              (selectedOption) => selectedOption.value === option.value
           )
         : selected;
   };

   const removeSelection = (value: string) => {
      var newSelections = selectedOptions as SelectOption[];
      const index = newSelections.findIndex((option) => option.value === value);
      newSelections.splice(index, 1);
      setSelectedOptions(newSelections);
      var event = {
         target: {name, value: newSelections},
      } as unknown as React.SyntheticEvent;
      onChange && onChange(event);
   };

   const toggleDropdown = React.useCallback(() => {
      if (disabled) return;
      setIsOpen((open) => !open);
   }, [disabled]);

   const handleFocusEvent = (e: React.FocusEvent<HTMLInputElement>) => {
      // TODO: FRON-685 Find Better Way to support Blur Event in Lux Select
      const relatedTarget = e.relatedTarget;
      const focusMovedToDropdownOption =
         relatedTarget instanceof Node &&
         (dropdownRef.current?.contains(relatedTarget) ||
            (relatedTarget instanceof HTMLElement &&
               (relatedTarget.id.startsWith(`${listboxId}-option-`) ||
                  relatedTarget.id.startsWith("headlessui-combobox-option"))));

      if (!focusMovedToDropdownOption) {
         var event = e;
         event.target.name = name || "";
         onBlur && onBlur(event);
      }
   };

   const handleComboboxKeyDown = React.useCallback(
      (e: React.KeyboardEvent | KeyboardEvent) => {
         if (disabled) return;

         if (e.key === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            if (!isOpen) {
               setIsOpen(true);
               return;
            }
            if (dropdownOptionCount > 0) {
               setHighlightedIndex((prev) => Math.min(prev + 1, dropdownOptionCount - 1));
            }
            return;
         }

         if (e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            if (!isOpen) {
               setIsOpen(true);
               return;
            }
            if (dropdownOptionCount > 0) {
               setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            }
            return;
         }

         if (e.key === "Enter" && isOpen && dropdownOptionCount > 0) {
            e.preventDefault();
            e.stopPropagation();
            if (showCustomValueOption) {
               onSelectChange({value: query, label: query});
            } else {
               onSelectChange(filteredOptions[highlightedIndex]);
            }
            return;
         }

         if (e.key === "Escape" && isOpen) {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(false);
            return;
         }

         if (!isOpen && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsOpen(true);
         }
      },
      [
         disabled,
         isOpen,
         dropdownOptionCount,
         highlightedIndex,
         showCustomValueOption,
         query,
         filteredOptions,
      ]
   );

   useEffect(() => {
      if (!isOpen || disabled) return;

      const handleDocumentKeyDown = (e: KeyboardEvent) => {
         if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) return;

         const target = e.target as Node;
         const inCombobox = comboboxContainerRef.current?.contains(target);
         const inDropdown = dropdownRef.current?.contains(target);
         if (!inCombobox && !inDropdown) return;

         handleComboboxKeyDown(e);
      };

      document.addEventListener("keydown", handleDocumentKeyDown, true);
      return () => document.removeEventListener("keydown", handleDocumentKeyDown, true);
   }, [isOpen, disabled, handleComboboxKeyDown]);

   return (
      <>
         <Combobox
            value={selectedOptions as SelectOption | SelectOption[] | undefined}
            onChange={onSelectChange as (value: SelectOption | SelectOption[] | null) => void}
            multiple={isMulti || false}
            disabled={disabled}
            name={id}
         >
            <div className={clsx("relative")} ref={comboboxContainerRef}>
               {label && tooltipText && (
                  <Tooltip content={tooltipText}>
                     <Combobox.Label
                        className={clsx("mb-[6px]", "block", "text-sm", "font-medium", "text-800")}
                     >
                        {label + (required ? " *" : "")}
                        <Icon icon="circle-question" className="ml-2" />
                     </Combobox.Label>
                  </Tooltip>
               )}
               {label && !tooltipText && (
                  <Combobox.Label
                     className={clsx("mb-[6px]", "block", "text-sm", "font-medium", "text-800")}
                  >
                     {label + (required ? " *" : "")}
                  </Combobox.Label>
               )}
               <div
                  className={clsx(
                     "relative",
                     "w-full",
                     "cursor-default",
                     "overflow-hidden",
                     "text-left",
                     "[&>div]:!bg-white",
                     "dark:[&>div]:!bg-gray_comp",
                     "dark:[&>div]:border-gray-600",
                     "[&>div>span]:leading-6",
                     "[&>div>input]:leading-6",
                     "[&>div>input]:py-0",
                     "focus:outline-none",
                     "focus-visible:ring-2",
                     "focus-visible:ring-white",
                     "dark:focus-visible:ring-gray-600",
                     "focus-visible:ring-opacity-75",
                     "focus-visible:ring-offset-2",
                     "focus-visible:ring-offset-white",
                     "dark:focus-visible:ring-offset-gray-600",
                     "sm:text-sm",
                     {
                        "dark:[&>div]:text-600 dark:[&>div]:border-gray-800": disabled,
                        "cursor-not-allowed opacity-50": disabled,
                     }
                  )}
               >
                  <Combobox.Input
                     as={ComboBoxDisplay}
                     onBlur={handleFocusEvent}
                     onClick={toggleDropdown}
                     onKeyDown={(e) => {
                        if (!isOpen) handleComboboxKeyDown(e);
                     }}
                     aria-activedescendant={
                        isOpen && dropdownOptionCount > 0
                           ? `${listboxId}-option-${highlightedIndex}`
                           : undefined
                     }
                     aria-controls={isOpen ? `${listboxId}-listbox` : undefined}
                     aria-expanded={isOpen}
                     aria-label={ariaLabel}
                     selectedOptions={selectedOptions}
                     placeHolder={placeHolder || "Search..."}
                     isMulti={isMulti}
                     query={query}
                     error={error}
                     warning={warning}
                     value={
                        isMulti ? (value as SelectOption[]) : options.find((o) => o.value === value)
                     }
                     removeSelection={removeSelection}
                     setQuery={setQuery}
                  />
                  {onChange && isClearable && (
                     <Combobox.Button
                        aria-label={ct("clear")}
                        className={clsx(
                           "absolute",
                           "inset-y-0",
                           "right-6",
                           "flex",
                           "items-center pr-2",
                           {
                              "dark:text-gray_comp": disabled,
                           }
                        )}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                           e.stopPropagation();
                           e.preventDefault();
                           /*setSelectedOptions(isMulti ? [] : ({} as SelectOption));*/
                           onChange({
                              target: {name, value: ""},
                           } as unknown as React.SyntheticEvent);
                        }}
                     >
                        <Icon icon={"xmark"} size={ICON_SIZES.BASE} />
                     </Combobox.Button>
                  )}
                  <Combobox.Button
                     type="button"
                     className={clsx(
                        "absolute",
                        "inset-y-0",
                        "right-0",
                        "flex",
                        "items-center pr-2",
                        {
                           "dark:text-gray_comp": disabled,
                        }
                     )}
                     onClick={toggleDropdown}
                  >
                     <Icon icon={"angle-down"} size={ICON_SIZES.BASE} />
                  </Combobox.Button>
               </div>
               {isOpen &&
                  createPortal(
                     <div
                        ref={dropdownRef}
                        className={clsx(
                           "fixed",
                           "rounded-md",
                           "bg-panel",
                           "dark:bg-gray-800",
                           isInsideDialog ? "z-[9999]" : "z-[100]",
                           "shadow-lg",
                           "ring-1",
                           "ring-black",
                           "dark:ring-gray-600",
                           "ring-opacity-5",
                           "pointer-events-auto"
                        )}
                     >
                        <div
                           id={`${listboxId}-listbox`}
                           ref={scrollContainerRef}
                           role="listbox"
                           className={clsx(
                              "max-h-60",
                              "space-y-0.5",
                              "overflow-auto",
                              "py-1",
                              "text-base",
                              "focus:outline-none",
                              "sm:text-sm"
                           )}
                        >
                           {filteredOptions.length === 0 && query !== "" ? (
                              !!allowCustomValue && query.length > 0 ? (
                                 <button
                                    id={`${listboxId}-option-0`}
                                    type="button"
                                    role="option"
                                    aria-selected={false}
                                    className={clsx(
                                       "relative",
                                       "w-full",
                                       "text-left",
                                       "cursor-pointer",
                                       "select-none",
                                       "rounded-lg",
                                       "py-2",
                                       "px-3",
                                       "text-800",
                                       "pointer-events-auto",
                                       "border-none",
                                       "bg-transparent",
                                       highlightedIndex === 0 && "ring-2 ring-primary/40"
                                    )}
                                    onMouseDown={(e) => {
                                       e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                       e.preventDefault();
                                       e.stopPropagation();
                                       onSelectChange({value: query, label: query});
                                    }}
                                 >
                                    &quot;{query}&quot;
                                 </button>
                              ) : (
                                 <div
                                    className={clsx(
                                       "relative",
                                       "cursor-default",
                                       "select-none",
                                       "py-2",
                                       "px-3",
                                       "text-800"
                                    )}
                                 >
                                    {ct("nothingFound")}
                                 </div>
                              )
                           ) : (
                              filteredOptions.map((option, optionIndex) => (
                                 <button
                                    id={`${listboxId}-option-${optionIndex}`}
                                    key={option.value}
                                    type="button"
                                    role="option"
                                    aria-selected={checkIfSelected(false, option)}
                                    className={clsx(
                                       "relative",
                                       "w-full",
                                       "text-left",
                                       "cursor-pointer",
                                       "select-none",
                                       "rounded-lg",
                                       "py-2",
                                       "px-3",
                                       "pointer-events-auto",
                                       "border-none",
                                       highlightedIndex === optionIndex &&
                                          "ring-2 ring-primary/40",
                                       checkIfSelected(false, option)
                                          ? "bg-[#CDD5E0] text-[#677489] dark:bg-gray-600 dark:text-gray-200"
                                          : "bg-transparent text-800 hover:bg-gray-400 hover:text-white dark:hover:bg-gray-700"
                                    )}
                                    onMouseDown={(e) => {
                                       e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                       e.preventDefault();
                                       e.stopPropagation();
                                       onSelectChange(option);
                                    }}
                                 >
                                    <span
                                       className={clsx(
                                          "block",
                                          "w-full",
                                          typeof option.label === "string" && "truncate",
                                          checkIfSelected(false, option) && "pr-6",
                                          checkIfSelected(false, option)
                                             ? "font-medium"
                                             : "font-normal"
                                       )}
                                    >
                                       {option.icon && (
                                          <Icon
                                             icon={option.icon}
                                             size={ICON_SIZES.LG}
                                             className="mr-2"
                                          />
                                       )}
                                       {option.label}
                                    </span>
                                    {checkIfSelected(false, option) ? (
                                       <span
                                          className={clsx(
                                             "absolute",
                                             "top-2",
                                             "right-0",
                                             "flex",
                                             "items-center",
                                             "pr-3"
                                          )}
                                       >
                                          <Icon
                                             icon={"circle-check"}
                                             type={ICON_TYPE.SOLID}
                                             size={ICON_SIZES.LG}
                                          />
                                       </span>
                                    ) : null}
                                 </button>
                              ))
                           )}
                        </div>
                        {/* Scroll indicator */}
                        {canScrollDown && (
                           <div
                              className={clsx(
                                 "absolute",
                                 "bottom-0",
                                 "left-0",
                                 "right-0",
                                 "h-8",
                                 "pointer-events-none",
                                 "bg-gradient-to-t",
                                 "from-panel",
                                 "dark:from-gray-800",
                                 "to-transparent",
                                 "rounded-b-md",
                                 "flex",
                                 "items-end",
                                 "justify-center",
                                 "pb-1"
                              )}
                           >
                              <Icon
                                 icon="chevron-down"
                                 size={ICON_SIZES.SM}
                                 className="text-muted-foreground animate-bounce"
                              />
                           </div>
                        )}
                     </div>,
                     document.body
                  )}
            </div>
         </Combobox>
         {helperText && (
            <p
               className={clsx(
                  "flex",
                  "py-2",
                  "text-xs",
                  error ? "text-[#db271b]" : warning ? "text-[#f1b825]" : "text-gray-500"
               )}
            >
               {helperText}
            </p>
         )}
      </>
   );
};

export default Select;
